import { AnsweringClient } from "../../clients/answeringClient";
import { composeShareText } from "../../utils/accessCodeShare";
import { describeApiError, isExpiredSession } from "../../utils/apiError";
import { copyToClipboard } from "../../utils/clipboard";
import { IManagedUser } from "../types";

export interface ManagedUsersControllerDelegate {
  didGetUsers(users: IManagedUser[]): void;
  filter({
    page,
    total,
    filter,
  }: {
    page: number;
    total: number;
    filter: string;
  }): void;
  applicationShouldLogout(): void;
  genericError(message?: string): void;
  didGetAssignmentToken(token: string): void;
  /** The share text is on the clipboard. */
  didCopyShareText(): void;
  /**
   * The current code for one assignment, straight from the reveal endpoint.
   * `null` means revealed-and-absent (no code row yet, or a login-mode
   * respondent) — distinct from "not asked yet", which the caller tracks by the
   * assignment simply not being present.
   */
  didRevealAccessCode(assignmentId: number, code: string | null): void;
  /** The backend accepted the send; the e-mail is on its way. */
  didSendShareEmail(): void;
  /**
   * A new code exists and the lockout is cleared. Distinct from a generic
   * success because the listing's status must be refetched, and because the
   * wording must not promise revoking access — a session already minted with
   * the old code stays alive until its token expires.
   */
  didRegenerateCode(assignmentId: number, code: string): void;
  /**
   * The respondent's state changed under the psychologist (409): retrying the
   * same action cannot help — regenerating is the way forward.
   */
  shareStateChanged(message: string): void;
}

export class ManagedUsersController {
  constructor(
    private delegate: ManagedUsersControllerDelegate,
    private readonly answeringClient = new AnsweringClient()
  ) {}

  public async getManagedUsers(
    accessToken: string,
    options?: { page: number; limit: number },
    hasRetried = false
  ): Promise<void> {
    try {
      const response = await this.answeringClient.getManagedUsers(
        accessToken,
        options
      );
      const users: IManagedUser[] = response.data.data.map((userData: any) => {
        const userCopy = userData;
        userCopy.assignments = userCopy.assignments.map(
          (assignmentData: any) => {
            const assignmentCopy = assignmentData;
            assignmentCopy.dueDate =
              assignmentCopy.dueDate && new Date(assignmentCopy.dueDate);
            assignmentCopy.startedAt =
              assignmentCopy.startedAt && new Date(assignmentCopy.startedAt);
            assignmentCopy.finishedAt =
              assignmentCopy.finishedAt && new Date(assignmentCopy.finishedAt);
            return assignmentCopy;
          }
        );
        return userCopy;
      });

      this.delegate.didGetUsers(users);
      this.delegate.filter({
        page: response.data.page,
        total: response.data.total,
        filter: response.data.filter || "",
      });
    } catch (error: any) {
      if (isExpiredSession(error) && !hasRetried) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          return;
        }
        return this.getManagedUsers(newTokens.accessToken, options, true);
      }
      this.delegate.genericError(
        describeApiError(error, "Não foi possível carregar os usuários.")
      );
    }
  }

  public async getAssignmentFeedbackToken(
    accessToken: string,
    assignmentId: number,
    hasRetried = false
  ): Promise<void> {
    try {
      const response = await this.answeringClient.getAssignmentFeedbackToken(
        accessToken,
        assignmentId
      );
      const token = response.data.token;
      this.delegate.didGetAssignmentToken(token);
    } catch (error: any) {
      //401 token expirado ou invalido, tentar refresh - aguardando mudanca no backend
      if (isExpiredSession(error) && !hasRetried) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          return;
        }
        return this.getAssignmentFeedbackToken(
          newTokens.accessToken,
          assignmentId,
          true
        );
      }
      if (error?.response?.status === 422) {
        this.delegate.genericError("O acesso à devolutiva está inativo nesta aplicação.");
        return;
      }
      this.delegate.genericError(
        describeApiError(error, "Não foi possível abrir a devolutiva.")
      );
    }
  }

  /**
   * Fetches the share pieces and puts the composed text on the clipboard. The
   * endpoint is called per click rather than cached, because it is the only
   * place the code value exists outside the database and because `accessMode`
   * is resolved fresh — the respondent may have registered since the listing
   * loaded, and then the share must carry no code.
   */
  public async copyAccessCodeShare(
    accessToken: string,
    assignmentId: number,
    hasRetried = false
  ): Promise<void> {
    try {
      const response = await this.answeringClient.getAccessCode(
        accessToken,
        assignmentId
      );
      // Throws when a code-mode payload lacks its code; surfaced as an error
      // rather than copying a half-written message.
      await copyToClipboard(composeShareText(response.data));
      this.delegate.didCopyShareText();
    } catch (error: any) {
      if (isExpiredSession(error) && !hasRetried) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          return;
        }
        return this.copyAccessCodeShare(
          newTokens.accessToken,
          assignmentId,
          true
        );
      }
      this.delegate.genericError(
        describeApiError(
          error,
          "Não foi possível obter o código desta atribuição."
        )
      );
    }
  }

  /**
   * Same endpoint as the share, without the clipboard: hands the raw code back
   * so the menu can show what is in force before the psychologist decides to
   * replace it. Called on an explicit "ver", not on menu open, so opening the
   * row menu for something else does not reveal a credential.
   */
  public async revealAccessCode(
    accessToken: string,
    assignmentId: number,
    hasRetried = false
  ): Promise<void> {
    try {
      const response = await this.answeringClient.getAccessCode(
        accessToken,
        assignmentId
      );
      this.delegate.didRevealAccessCode(
        assignmentId,
        response.data.code ?? null
      );
    } catch (error: any) {
      if (isExpiredSession(error) && !hasRetried) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          return;
        }
        return this.revealAccessCode(newTokens.accessToken, assignmentId, true);
      }
      this.delegate.genericError(
        describeApiError(
          error,
          "Não foi possível obter o código desta atribuição."
        )
      );
    }
  }

  /**
   * Asks the backend to e-mail the share. The two failure modes are kept apart
   * on purpose: 409 means the recipient's state no longer matches and only
   * regenerating moves forward, while 503 is a transient delivery failure worth
   * retrying. Collapsing them into one message would send the psychologist to
   * retry something that can never succeed.
   */
  public async sendAccessCodeEmail(
    accessToken: string,
    assignmentId: number,
    hasRetried = false
  ): Promise<void> {
    try {
      await this.answeringClient.resendAccessCode(accessToken, assignmentId);
      this.delegate.didSendShareEmail();
    } catch (error: any) {
      if (isExpiredSession(error) && !hasRetried) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          return;
        }
        return this.sendAccessCodeEmail(
          newTokens.accessToken,
          assignmentId,
          true
        );
      }
      if (error?.response?.status === 409) {
        this.delegate.shareStateChanged(
          describeApiError(
            error,
            "Não foi possível compartilhar: gere um novo código para esta atribuição."
          )
        );
        return;
      }
      this.delegate.genericError(
        describeApiError(
          error,
          "Não foi possível enviar o e-mail agora. Tente novamente."
        )
      );
    }
  }

  /** New code value plus lockout reset — see `didRegenerateCode`. */
  public async regenerateAccessCode(
    accessToken: string,
    assignmentId: number,
    hasRetried = false
  ): Promise<void> {
    try {
      const response = await this.answeringClient.regenerateAccessCode(
        accessToken,
        assignmentId
      );
      // Carries the assignmentId so a code already on screen is replaced rather
      // than left showing the value that was just invalidated.
      this.delegate.didRegenerateCode(assignmentId, response.data.code);
    } catch (error: any) {
      if (isExpiredSession(error) && !hasRetried) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          return;
        }
        return this.regenerateAccessCode(
          newTokens.accessToken,
          assignmentId,
          true
        );
      }
      if (error?.response?.status === 409) {
        this.delegate.shareStateChanged(
          describeApiError(
            error,
            "Esta pessoa já possui conta — ela entra com e-mail e senha."
          )
        );
        return;
      }
      this.delegate.genericError(
        describeApiError(error, "Não foi possível gerar um novo código.")
      );
    }
  }

  private async refreshTokens(): Promise<{
    accessToken: string;
    refreshToken: string;
  } | void> {
    const refreshToken = localStorage.getItem("RefreshToken");
    if (!refreshToken) {
      this.delegate.applicationShouldLogout();
      return;
    }
    try {
      const response = await this.answeringClient.refreshToken(refreshToken);
      const tokens: { accessToken: string; refreshToken: string } =
        response.data;
      localStorage.setItem("AccessToken", tokens.accessToken);
      localStorage.setItem("RefreshToken", tokens.refreshToken);
      return tokens;
    } catch (error: any) {
      this.delegate.applicationShouldLogout();
      return;
    }
  }
}
